/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable id-length */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { EmptyResult } from '@package/services/src/Result'

import { SubOrder } from '../shoppingCart/types'

import { GoogleAnalyticsCheckoutTypes } from './enums'
import { GoogleAnalyticsService } from './services'
import {
  PageviewRequest,
  CheckoutProcessRequest,
  AddProductToCartRequest,
  AddSelectedPaymentRequest,
  AddProductServiceToCartRequest,
  RemoveProductFromCartRequest,
  PurchasesRequest,
} from './types'

export default class GoogleAnalyticApisService implements GoogleAnalyticsService {
  public constructor() { }

  private dataLayer =
    typeof window !== 'undefined' ? window.dataLayer : undefined

  private mapShoppingCartProducts(subOrders: Array<SubOrder>) {
    return subOrders.flatMap((suborder) =>
      suborder.products?.map((product: any) => ({
        id: product.id || product.seq,
        name: product.name,
        price: `${product.price}`,
        quantity: product.quantity,
      }))
    )
  }

  private clearEcommerceObject() {
    if (this.dataLayer) this.dataLayer.push({ ecommerce: null })
  }

  public async pageview(request: PageviewRequest): Promise<EmptyResult> {
    const { pageUrl } = request

    if (!this.dataLayer) {
      console.warn('Google analytics was not provided')
      return new EmptyResult()
    }

    this.clearEcommerceObject()
    this.dataLayer.push({
      event: 'pageview',
      page: (pageUrl || window.location.pathname) || '',
    })

    return new EmptyResult()
  }

  public async initCheckoutProcess(
    request: CheckoutProcessRequest
  ): Promise<EmptyResult> {
    const { checkoutType, shoppingCart } = request

    if (!this.dataLayer) {
      console.warn('Google analytics was not provided')
      return new EmptyResult()
    }

    this.clearEcommerceObject()

    if (shoppingCart) {
      const products = this.mapShoppingCartProducts(shoppingCart.subOrders)

      if (checkoutType === GoogleAnalyticsCheckoutTypes.ShoppingCart) {
        this.dataLayer.push({
          event: 'checkout',
          ecommerce: {
            checkout: {
              actionField: {
                step: GoogleAnalyticsCheckoutTypes.ShoppingCart,
              },
              products,
            },
            promoView: null,
          },
        })

        return new EmptyResult()
      }

      this.dataLayer.push({
        event: 'checkout',
        ecommerce: {
          checkout: {
            actionField: {
              step: checkoutType,
            },
            products,
          },
          promoView: null,
        },
      })

    }

    if (checkoutType === GoogleAnalyticsCheckoutTypes.Confirmation) {
      this.dataLayer.push({
        event: 'checkout',
        ecommerce: {
          checkout: {
            actionField: {
              step: GoogleAnalyticsCheckoutTypes.Confirmation,
            },
            products: [],
          },
          promoView: null,
        },
      })

      return new EmptyResult()
    }

    return new EmptyResult()
  }

  // Can be two types of product - Product and ProductAddonElement
  public async addProductToCart(
    request: AddProductToCartRequest
  ): Promise<EmptyResult> {
    const { product } = request

    if (!this.dataLayer) {
      return new EmptyResult()
    }

    if ('quantity' in product) {
      const item = {
        id: product.id || product.seq,
        name: product.name,
        price: `${product.price}`,
        quantity: product.quantity,
      }

      this.clearEcommerceObject()
      this.dataLayer.push({
        event: 'addToCart',
        ecommerce: {
          currencyCode: 'USD',
          add: {
            products: [item],
          },
        },
      })

      return new EmptyResult()
    }

    const item = {
      id: product.sku,
      name: product.name,
      price: `${product.price}`,
    }

    this.clearEcommerceObject()
    this.dataLayer.push({
      event: 'addToCart',
      ecommerce: {
        currencyCode: 'USD',
        add: {
          products: [item],
        },
      },
    })

    return new EmptyResult()
  }

  public async addSelectedPayment(
    request: AddSelectedPaymentRequest
  ): Promise<EmptyResult> {
    const { paymentType } = request

    if (!this.dataLayer) {
      console.warn('Google analytics was not provided')
      return new EmptyResult()
    }

    this.clearEcommerceObject()
    this.dataLayer.push({
      event: 'checkoutOption',
      ecommerce: {
        checkout_option: {
          actionField: {
            step: GoogleAnalyticsCheckoutTypes.Payment,
            option: paymentType,
          },
        },
      },
    })

    return new EmptyResult()
  }

  // Can be two types of services - ProductServicesElement or CustomConfigurationElement
  public async addProductServiceToCart(
    request: AddProductServiceToCartRequest
  ): Promise<EmptyResult> {
    const { product } = request

    if (!this.dataLayer) {
      console.warn('Google analytics was not provided')
      return new EmptyResult()
    }

    const item = {
      id: product.id,
      name: product.name,
      price: `${product.price}`,
    }

    this.clearEcommerceObject()
    this.dataLayer.push({
      event: 'addToCart',
      ecommerce: {
        currencyCode: 'USD',
        add: {
          products: [item],
        },
      },
    })

    return new EmptyResult()
  }

  public async removeProductFromCart(
    request: RemoveProductFromCartRequest
  ): Promise<EmptyResult> {
    const { product } = request

    if (!this.dataLayer) {
      return new EmptyResult()
    }

    const item = {
      id: product.id || product.seq,
      name: product.name,
      price: `${product.price}`,
    }

    this.clearEcommerceObject()
    this.dataLayer.push({
      event: 'removeFromCart',
      ecommerce: {
        remove: {
          products: [item],
        },
      },
    })

    return new EmptyResult()
  }

  public async purchases(request: PurchasesRequest): Promise<EmptyResult> {
    const { submitData, couponCode } = request

    submitData.forEach((data) => {
      if (!this.dataLayer) {
        return console.warn('Google analytics was not provided')
      }

      const items = data.products.map((product) => {
        /**
         * brand category and variant are optional
         * so, we have to send them to the GA if they're available
         */
        const purchaseOptionalValues: Record<string, string | undefined> = Object.fromEntries(
          Object.entries({
            brand: product.brand,
            category: product.category,
            variant: product.variant,
          }).filter(([_, value]) => value)
        )
        return {
          id: product.sku,
          name: product.name,
          price: `${product.price}`,
          quantity: product.quantity,
          ...purchaseOptionalValues,
        }
      })

      this.clearEcommerceObject()
      this.dataLayer.push({
        event: 'transaction',
        ecommerce: {
          purchase: {
            actionField: {
              id: `${data.orderNumber}`,
              affiliation: data.vendorName,
              revenue: `${data.orderTotal}`,
              tax: `${data.taxTotal}`,
              shipping: `${data.shippingTotal}`,
              coupon: couponCode.couponNumber || '',
            },
            products: items,
          },
        },
      })
    })

    return new EmptyResult()
  }
}
